/**
 * useStatsSession — owns the actions log and the operations on it.
 *
 * Extracted from EstadisticasNew.js:
 *  - actionsLog / lastAction / gameShotChartData / activeQualifiers / selectedActionIndex state
 *  - recordAction (with mode-aware timestamping & qualifier attachment)
 *  - undoLastAction (rollback last action + score)
 *  - deleteAction (optimistic delete + auto-save to backend + undo callback)
 *  - updateActionTimestamp (optimistic edit + auto-save + rollback on error)
 *  - handleShotChartRecorded (push court coords into gameShotChartData)
 *  - recordOpponentScore (used by the +1/+2/+3 buttons in the score panel)
 *
 * The hook is intentionally decoupled from the clock and the video player.
 * Callers pass:
 *   - statsMode                — 'live' | 'video'
 *   - currentPeriod            — number
 *   - computeTimestamp         — () => seconds (live: realTimeRef; video: currentTime+offset)
 *   - t                        — i18n translator (kept verbatim with original keys)
 *   - onScoreChange(delta)     — invoked only in LIVE mode for +/- score updates
 *   - onShotAction(payload)    — invoked when a shot action should open the shot chart
 *   - onMessage(msg, sev, undo)— snackbar callback
 */

import { useState, useCallback } from 'react';
import api from '../../../api/axiosConfig';
import { ACTION_CODES, getActionLabel } from '../../utils/actionUtils';
import { STATS_MODE, SHOT_ACTION_CODES, FREE_THROW_CODES } from '../constants';

export function useStatsSession({
  statsMode,
  currentPeriod,
  computeTimestamp,
  t,
  onScoreChange,
  onShotAction,
  onMessage,
}) {
  const [actionsLog, setActionsLog] = useState([]);
  const [lastAction, setLastAction] = useState(null);
  const [gameShotChartData, setGameShotChartData] = useState([]);
  const [activeQualifiers, setActiveQualifiers] = useState([]);
  const [selectedActionIndex, setSelectedActionIndex] = useState(null);

  const message = useCallback((msg, severity = 'info', action = null) => {
    if (typeof onMessage === 'function') onMessage(msg, severity, action);
  }, [onMessage]);

  const pointsForCode = useCallback((code) => {
    if (code === ACTION_CODES.TWO_PTS_MADE) return 2;
    if (code === ACTION_CODES.THREE_PTS_MADE) return 3;
    if (code === ACTION_CODES.FREE_THROW_MADE) return 1;
    return 0;
  }, []);

  /**
   * Record a player action. Mirrors recordAction() from EstadisticasNew.js.
   */
  const recordAction = useCallback((player, actionCode, points = 0) => {
    const timestamp = typeof computeTimestamp === 'function' ? computeTimestamp() : 0;
    const actionLabel = getActionLabel(actionCode, t);
    const isShotAction = SHOT_ACTION_CODES.includes(actionCode);

    const newAction = {
      playerId: player.id,
      playerName: player.nom,
      action: actionLabel,
      actionCode,
      period: currentPeriod,
      timeSec: Math.floor(timestamp),
      videoTimestamp: statsMode === STATS_MODE.VIDEO ? timestamp : null,
      realTimeTimestamp: statsMode === STATS_MODE.LIVE ? timestamp : null,
      isVideoMode: statsMode === STATS_MODE.VIDEO,
      isLiveMode: statsMode === STATS_MODE.LIVE,
      ...(isShotAction && activeQualifiers.length > 0 ? { qualifiers: [...activeQualifiers] } : {}),
    };

    setActionsLog((prev) => [...prev, newAction]);
    setLastAction(newAction);

    // Score increment is LIVE-only — VIDEO mode reads the score from existing game data
    if (points > 0 && statsMode === STATS_MODE.LIVE && typeof onScoreChange === 'function') {
      onScoreChange(points);
    }

    // Open the shot chart for non-FT shot actions in video mode
    const isFreeThrow = FREE_THROW_CODES.includes(actionCode);
    if (isShotAction && !isFreeThrow && statsMode === STATS_MODE.VIDEO && typeof onShotAction === 'function') {
      onShotAction({
        player,
        actionCode,
        made: actionCode.includes('MADE'),
        timeSec: Math.floor(timestamp),
        period: currentPeriod,
      });
    }

    return newAction;
  }, [statsMode, currentPeriod, computeTimestamp, t, onScoreChange, onShotAction, activeQualifiers]);

  /**
   * Live-mode opponent score recording (+1 / +2 / +3 buttons in the score panel).
   * Mirrors the inline handler in renderLiveMode().
   */
  const recordOpponentScore = useCallback((actionCode) => {
    const timestamp = typeof computeTimestamp === 'function' ? computeTimestamp() : 0;
    setActionsLog((prev) => [...prev, {
      playerId: 0,
      playerName: t('estadisticasNew.opponentScore'),
      action: getActionLabel(actionCode, t),
      actionCode,
      period: currentPeriod,
      timeSec: Math.floor(timestamp),
      videoTimestamp: statsMode === STATS_MODE.VIDEO ? timestamp : undefined,
      realTimeTimestamp: statsMode === STATS_MODE.LIVE ? timestamp : undefined,
      isLiveMode: statsMode === STATS_MODE.LIVE,
      isVideoMode: statsMode === STATS_MODE.VIDEO,
    }]);
  }, [statsMode, currentPeriod, computeTimestamp, t]);

  /**
   * Undo the last recorded action. Mirrors undoLastAction().
   */
  const undoLastAction = useCallback(() => {
    if (!lastAction) return;
    setActionsLog((prev) => prev.filter((a) => a !== lastAction));

    const code = lastAction.actionCode || lastAction.action;
    const points = pointsForCode(code);
    if (points > 0 && statsMode === STATS_MODE.LIVE && typeof onScoreChange === 'function') {
      onScoreChange(-points);
    }

    message(t('estadisticasNew.actionUndone'), 'info');
    setLastAction(null);
  }, [lastAction, statsMode, onScoreChange, message, t, pointsForCode]);

  /**
   * Persist the court (x, y) for a shot. Mirrors handleShotChartRecorded.
   * The active qualifiers at the moment of recording are attached.
   */
  const recordShotChart = useCallback((shot, shotChartAction) => {
    if (!shotChartAction) return;
    const shotEntry = {
      player_id: shotChartAction.player.id,
      action_code: shotChartAction.actionCode,
      court_x: shot.court_x,
      court_y: shot.court_y,
      made: shot.made,
      period: shotChartAction.period,
      time_sec: shotChartAction.timeSec,
      zone: shot.zone,
      qualifiers: activeQualifiers.length > 0 ? activeQualifiers : null,
    };
    setGameShotChartData((prev) => [...prev, shotEntry]);
  }, [activeQualifiers]);

  /**
   * Delete a specific action with optimistic update + auto-save + undo.
   * Mirrors deleteAction().
   */
  const deleteAction = useCallback(async (actionIndex, gameId) => {
    if (actionIndex < 0 || actionIndex >= actionsLog.length) return;

    const actionToDelete = actionsLog[actionIndex];
    const previousActionsLog = [...actionsLog];
    const updatedActionsLog = actionsLog.filter((_, idx) => idx !== actionIndex);
    setActionsLog(updatedActionsLog);

    const code = actionToDelete.actionCode || actionToDelete.action;
    const points = pointsForCode(code);
    if (points > 0 && statsMode === STATS_MODE.LIVE && typeof onScoreChange === 'function') {
      onScoreChange(-points);
    }

    if (gameId && actionToDelete.playerId) {
      try {
        const playerActions = updatedActionsLog.filter((a) => a.playerId === actionToDelete.playerId);

        if (playerActions.length === 0) {
          await api.delete(`/api/stats/actions/${gameId}/${actionToDelete.playerId}`);
        } else {
          await api.put(`/api/stats/actions/${gameId}/${actionToDelete.playerId}`, {
            actionIndex: -1,
            newTimestamp: 0,
            actions: playerActions.map((a) => ({
              action: a.action,
              actionCode: a.actionCode || a.action,
              period: a.period,
              timeSec: a.timeSec,
              videoTimestamp: a.videoTimestamp,
            })),
          });
        }

        const undoDelete = () => {
          setActionsLog(previousActionsLog);
          if (points > 0 && statsMode === STATS_MODE.LIVE && typeof onScoreChange === 'function') {
            onScoreChange(points);
          }
          const restoredPlayerActions = previousActionsLog.filter((a) => a.playerId === actionToDelete.playerId);
          api.put(`/api/stats/actions/${gameId}/${actionToDelete.playerId}`, {
            actionIndex: -1,
            newTimestamp: 0,
            actions: restoredPlayerActions.map((a) => ({
              action: a.action,
              actionCode: a.actionCode || a.action,
              period: a.period,
              timeSec: a.timeSec,
              videoTimestamp: a.videoTimestamp,
            })),
          }).catch((err) => console.error('Error restoring action:', err));
          message(t('estadisticasNew.actionUndone'), 'info');
        };

        message(
          t('estadisticasNew.actionDeleted', {
            player: actionToDelete.playerName,
            action: actionToDelete.action,
          }),
          'success',
          undoDelete,
        );
      } catch (error) {
        console.error('Error auto-saving after delete:', error);
        setActionsLog(previousActionsLog);
        if (points > 0 && statsMode === STATS_MODE.LIVE && typeof onScoreChange === 'function') {
          onScoreChange(points);
        }
        message(t('estadisticasNew.autoSaveError'), 'error');
      }
    } else {
      message(
        t('estadisticasNew.actionDeletedLocally', {
          player: actionToDelete.playerName,
          action: actionToDelete.action,
        }),
        'info',
      );
    }

    setSelectedActionIndex(null);
  }, [actionsLog, statsMode, onScoreChange, message, t, pointsForCode]);

  /**
   * Adjust the videoTimestamp of an existing action by `timeDelta` seconds,
   * with optimistic update + auto-save + rollback. Mirrors updateActionTimestamp().
   *
   * `playerSeekTo(time)` is the optional seek function from useVideoPlayer.
   */
  const updateActionTimestamp = useCallback(async (actionIndex, timeDelta, gameId, playerSeekTo) => {
    if (actionIndex < 0 || actionIndex >= actionsLog.length) return;

    const action = actionsLog[actionIndex];
    const previousActionsLog = [...actionsLog];
    const newTimestamp = Math.max(0, action.videoTimestamp + timeDelta);

    const updatedActionsLog = actionsLog.map((act, idx) => {
      if (idx === actionIndex) {
        return { ...act, videoTimestamp: newTimestamp, timeSec: Math.floor(newTimestamp) };
      }
      return act;
    });
    setActionsLog(updatedActionsLog);

    if (typeof playerSeekTo === 'function') {
      playerSeekTo(newTimestamp);
    }

    if (gameId && action.playerId) {
      try {
        const playerActions = updatedActionsLog.filter((a) => a.playerId === action.playerId);
        const originalAction = actionsLog[actionIndex];
        const playerActionIndex = playerActions.findIndex((a) =>
          a.action === originalAction.action &&
          a.period === originalAction.period &&
          a.videoTimestamp === newTimestamp,
        );

        const response = await api.put(`/api/stats/actions/${gameId}/${action.playerId}`, {
          actionIndex: playerActionIndex,
          newTimestamp,
          actions: playerActions.map((a) => ({
            action: a.action,
            actionCode: a.actionCode || a.action,
            period: a.period,
            timeSec: a.timeSec,
            videoTimestamp: a.videoTimestamp,
          })),
        });

        if (response.data && response.data.affectedRows > 0) {
          message(t('estadisticasNew.autoSaved'), 'success');
        }
      } catch (error) {
        console.error('Error auto-saving action:', error);
        setActionsLog(previousActionsLog);
        message(t('estadisticasNew.autoSaveError'), 'error');
      }
    }
  }, [actionsLog, message, t]);

  /**
   * Toggle one of the shot qualifiers (PAINT / FASTBREAK / SECOND_CHANCE).
   */
  const toggleQualifier = useCallback((qualifier) => {
    setActiveQualifiers((prev) =>
      prev.includes(qualifier) ? prev.filter((q) => q !== qualifier) : [...prev, qualifier],
    );
  }, []);

  const reset = useCallback(() => {
    setActionsLog([]);
    setLastAction(null);
    setGameShotChartData([]);
    setActiveQualifiers([]);
    setSelectedActionIndex(null);
  }, []);

  return {
    // state
    actionsLog,
    setActionsLog,
    lastAction,
    setLastAction,
    gameShotChartData,
    setGameShotChartData,
    activeQualifiers,
    setActiveQualifiers,
    toggleQualifier,
    selectedActionIndex,
    setSelectedActionIndex,

    // operations
    recordAction,
    recordOpponentScore,
    undoLastAction,
    recordShotChart,
    deleteAction,
    updateActionTimestamp,

    // utils
    pointsForCode,
    reset,
  };
}
